import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Printer, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Modal "Liste de chargement" pour un BT.
 * Affiche le matériel nécessaire par tour + agrégé pour tout le BT.
 * Bouton imprimer pour générer la feuille de chargement.
 */
export function ListeChargementDialog({ btId, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !btId) return;
    let cancel = false;
    setLoading(true);
    axios
      .get(`${API_URL}/api/crm/bons-travail/${btId}/liste-chargement`)
      .then((res) => { if (!cancel) setData(res.data); })
      .catch((e) => { if (!cancel) setData({ error: e?.response?.data?.detail || e.message }); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [open, btId]);

  const handlePrint = () => {
    const el = document.getElementById('liste-chargement-printable');
    if (!el) return;
    const w = window.open('', 'Print', 'width=900,height=800');
    w.document.write(`
      <html><head><title>Liste de chargement</title>
      <style>
        body{font-family:Arial,sans-serif;font-size:12px;padding:20px;color:#1e293b}
        h1{font-size:20px;margin:0 0 8px;color:#9a3412}
        h2{font-size:14px;margin:14px 0 4px;padding:4px 6px;background:#fff7ed;border-left:4px solid #f97316}
        .meta{background:#fff7ed;border:1px solid #fed7aa;padding:8px;border-radius:6px;margin:8px 0}
        table{border-collapse:collapse;width:100%;margin-top:6px}
        th,td{border:1px solid #cbd5e1;padding:6px 8px;text-align:left;font-size:11px}
        th{background:#fef3c7;font-weight:700}
        .check{width:24px;text-align:center}
        .warning{background:#fef2f2;border:1px solid #fca5a5;padding:8px;border-radius:6px;margin:8px 0;color:#991b1b}
        .qte{font-weight:700;text-align:right}
        .small{color:#64748b;font-size:10px}
        @media print { body { padding: 10px; } }
      </style>
      </head><body>${el.innerHTML}</body></html>
    `);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2">📋 Liste de chargement</span>
            {data && !data.error && (
              <Button size="sm" onClick={handlePrint} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="btn-imprimer-liste">
                <Printer className="w-3.5 h-3.5 mr-1" /> Imprimer
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-10 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement...
          </div>
        )}

        {data?.error && (
          <div className="bg-red-50 border border-red-200 p-3 rounded text-sm text-red-800">
            ⚠️ {data.error}
          </div>
        )}

        {data && !data.error && !loading && (
          <div id="liste-chargement-printable" className="space-y-4">
            {/* ─── Meta du BT ─── */}
            <div className="meta bg-orange-50 border border-orange-200 rounded p-3 space-y-0.5 text-sm">
              <h1 className="text-lg font-bold text-orange-900">Liste de chargement — BT #{data.bt?.numero || '—'}</h1>
              <p><strong>Client :</strong> {data.bt?.client_nom || '—'}</p>
              {data.bt?.adresse && <p><strong>Adresse :</strong> {data.bt.adresse}{data.bt.ville ? `, ${data.bt.ville}` : ''}</p>}
              {(data.bt?.dates_chantier || []).length > 0 && (
                <p><strong>Dates :</strong> {(data.bt.dates_chantier || []).join(', ')}</p>
              )}
              {data.remorque?.numero && <p className="small text-slate-500">Remorque : {data.remorque.numero}</p>}
            </div>

            {/* ─── Avertissements globaux ─── */}
            {data.avertissements_globaux?.length > 0 && (
              <div className="warning bg-red-50 border-2 border-red-300 p-3 rounded">
                <p className="font-bold text-red-900 flex items-center gap-1.5 mb-1">
                  <AlertTriangle className="w-4 h-4" /> Règles à vérifier avant chargement
                </p>
                <ul className="text-xs text-red-800 space-y-0.5 list-disc pl-5">
                  {data.avertissements_globaux.map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              </div>
            )}

            {/* ─── Total agrégé ─── */}
            <div>
              <h2 className="text-sm font-bold bg-orange-100 border-l-4 border-orange-500 px-2 py-1 text-orange-900">
                🎯 Total à charger ({data.total_agrege?.length || 0} types de pièces)
              </h2>
              {data.total_agrege?.length === 0 ? (
                <p className="text-xs text-slate-500 italic py-2">Aucune pièce — vérifiez la configuration des tours.</p>
              ) : (
                <table className="w-full mt-2 text-xs border-collapse">
                  <thead>
                    <tr className="bg-amber-100">
                      <th className="border border-slate-300 p-1.5 text-left w-8 check">✓</th>
                      <th className="border border-slate-300 p-1.5 text-left">Pièce</th>
                      <th className="border border-slate-300 p-1.5 text-left">Catégorie</th>
                      <th className="border border-slate-300 p-1.5 text-right w-16 qte">Qté</th>
                      <th className="border border-slate-300 p-1.5 text-right w-20">Stock</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.total_agrege.map((p, i) => (
                      <tr key={i} className={!p.disponible ? 'bg-red-50' : ''}>
                        <td className="border border-slate-300 p-1.5 check">☐</td>
                        <td className="border border-slate-300 p-1.5 font-medium">{p.nom}</td>
                        <td className="border border-slate-300 p-1.5 small text-slate-500">{p.categorie}</td>
                        <td className="border border-slate-300 p-1.5 qte text-right">{p.qte_necessaire}</td>
                        <td className={`border border-slate-300 p-1.5 text-right ${p.disponible ? 'text-emerald-700' : 'text-red-700 font-bold'}`}>
                          {p.disponible ? `✅ ${p.qte_disponible}` : `⚠️ ${p.qte_disponible}`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* ─── Détail par tour ─── */}
            {(data.tours || []).map((tour, i) => (
              <div key={i} className="bg-white border border-orange-200 rounded p-3">
                <h2 className="text-sm font-bold bg-orange-50 border-l-4 border-orange-400 px-2 py-1 text-orange-900 -mx-3 -mt-3 mb-2 rounded-t">
                  🏗 Tour #{tour.index} — {tour.zone_nom || `Zone ${tour.zone_numero}`}
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs mb-2">
                  <div><span className="text-slate-500">Type:</span> <strong>{tour.template_nom}</strong></div>
                  <div><span className="text-slate-500">Étages:</span> <strong>{tour.nb_etages}</strong></div>
                  <div><span className="text-slate-500">Mur:</span> <strong>{tour.distance_mur_inch}″</strong></div>
                  <div>
                    {tour.terrain_pente && <span className="text-amber-700 font-medium">⛰ Pente</span>}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-[11px]">
                  {(tour.pieces || []).map((p, pi) => (
                    <div key={pi} className={`flex items-center justify-between border-b border-slate-100 py-0.5 ${!p.disponible ? 'text-red-700 font-semibold' : 'text-slate-700'}`}>
                      <span><strong>{p.qte_necessaire}×</strong> {p.nom}</span>
                      <span className="text-slate-400 text-[10px]">{p.disponible ? '✓' : `⚠️ ${p.qte_disponible}`}</span>
                    </div>
                  ))}
                </div>
                {tour.etages_fixation?.length > 0 && (
                  <p className="text-[11px] text-slate-600 mt-2">📍 Fixation murale : étage(s) {tour.etages_fixation.join(', ')}</p>
                )}
                {tour.etages_equerre?.length > 0 && (
                  <p className="text-[11px] text-slate-600">📐 Équerre : étage(s) {tour.etages_equerre.join(', ')}</p>
                )}
              </div>
            ))}

            {(data.avertissements_globaux?.length || 0) === 0 && (data.total_agrege?.length || 0) > 0 && (
              <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 p-2 rounded">
                <CheckCircle2 className="w-3.5 h-3.5" /> Configuration conforme — prêt pour le chargement
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
